import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { Plus, Search } from 'lucide-react';
import { PageHeader } from '../PageHeader';
import { Button, Input, Tabs, Typography, type TabItem } from '../ui';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { contactPath, useWholeContactBook } from '../../../lib/contact-book';
import { defaultTab, type OutreachTab } from '../../../lib/contact-rows';
import { outreachUrlQuery, readOutreachUrlState, sameQuery, type OutreachStatusParam } from '../../../lib/outreach-url-state';
import { STAGE_GROUPS, type StageGroupId } from '../../../lib/outreach-stage';
import { useIslandState } from '../../../lib/use-island-state';
import { WaitingNoteRow } from '../WaitingNoteRow';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useNarrow } from '../../../lib/use-narrow';
import { useMobileActionBarHeight } from '../../../lib/use-mobile-action-bar-height';
import { useOutreachCallTabEnabled } from '../../../lib/outreach-call-tab-flag';
import { useTranslation } from '../../../lib/i18n';
import { ContactsBody, type ContactsListState } from './ContactsBody';
import { useProfileLinkLeads } from '../profile-links/ProfileLinkLeadsSection';
import { InboxBody } from './InboxBody';
import { CallsBody } from './CallsBody';
import { CategoryHeader, CategoryPage, CategoryToolbar } from '../category-view/CategoryView';
import styles from './contacts.module.css';
import { TabConnectHint } from '../category-view/TabConnectHint';
import { OutreachEmptyState } from '../category-view/CategoryEmptyState';
import { isHomeEmptyForced } from '../../../lib/dev-home-empty-states';

/**
 * The **Outreach** screen — two peer tabs, **Contacts** (the list) and **Inbox** (placeholder for a
 * later piece). Reached from the Your Agents "Outreach" tile; the route stays `/contacts` so the
 * deeplink and drill-in pages are unchanged. Shared 1:1 with iOS/Android `OutreachScreen`.
 *
 * The search + "Add a new contact" sit per-viewport, matching each Figma frame: **desktop** puts them
 * in the toolbar beside the tabs (web `17937:268706`); **mobile-web** drops the search full-width into
 * the list and pins "Add a new contact" at the foot over a gradient scrim, like the native apps
 * (`17937:264687`). The map-home Outreach section embeds `ContactsBody` directly (no tabs).
 *
 * The Contacts tab lists the WHOLE people book under the outreach-derived head (`wholeBook`). The
 * screen opens on the Inbox when there is correspondence to show and on Contacts when there is none
 * (`defaultTab`) — decided once, on the first real data, and never over a tab the reader picked.
 *
 * A third tab, **Calls** (`CallsBody`), follows Inbox behind `symphonyOutreachCallTab`: Alfred's Connect, or its
 * number once connected. It is never the default, and it carries no search and no "Add a new contact".
 */
/** `onHome`: hosted by the home's Outreach category — no page header, no add button of its own (the
 *  home hands its own in as `action`), and the tab and searches stay out of the URL, which belongs to
 *  the home. */
export function OutreachBody({ onHome, action }: { onHome?: boolean; action?: ReactNode } = {}) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const narrow = useNarrow();
  const { logPageClickBi } = useGenericBi();
  const [tab, setTab] = useState<OutreachTab>('contacts');
  // Two independent queries — one per tab. A single shared query leaked a Contacts search into the
  // Inbox (and back) on a tab switch; iOS models the same two fields (`search` / `inboxSearch`), and
  // each tab's search-started BI hook watches its OWN query.
  const [search, setSearch] = useState('');
  const [inboxSearch, setInboxSearch] = useState('');
  // The Inbox's Status filter, owned here so it rides the URL like the searches (`?status=needs_you`).
  const [inboxStatus, setInboxStatus] = useState<StageGroupId | null>(null);
  const tabPicked = useRef(false);
  const tabDecided = useRef(false);

  // The tab and both searches live in the URL (`lib/outreach-url-state.ts`), because opening a contact
  // is a page navigation that unmounts this body: without it, back from a contact opened on the
  // Contacts tab landed on the Inbox. Restored ONCE, when the router has the query — and declared
  // before the default-tab effect, so a restored tab is already decided by the time that rule runs.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || (!onHome && !router.isReady)) return;
    restored.current = true;
    if (onHome) return;
    const saved = readOutreachUrlState(router.query);
    if (saved.tab) {
      tabDecided.current = true;
      tabPicked.current = true;
      setTab(saved.tab);
    }
    if (saved.search) setSearch(saved.search);
    if (saved.inboxSearch) setInboxSearch(saved.inboxSearch);
    if (saved.status) setInboxStatus(statusFromParam(saved.status));
  }, [router.isReady, router.query, onHome]);

  // A NEW `?status=` arriving while this screen is already mounted — the island's "See all" or the Tasks note
  // pressed from /contacts itself — re-applies the filter (and the Inbox), which the once-only restore above would
  // otherwise ignore. Keyed on the URL value, so the screen's own write-back of the same value is a no-op.
  const statusParam = onHome ? undefined : readOutreachUrlState(router.query).status;
  const lastStatusParam = useRef(statusParam);
  useEffect(() => {
    if (!restored.current || statusParam === lastStatusParam.current) return;
    lastStatusParam.current = statusParam;
    if (!statusParam) return;
    setInboxStatus(statusFromParam(statusParam));
    tabDecided.current = true;
    tabPicked.current = true;
    setTab('inbox');
  }, [statusParam]);

  // The same read the Contacts tab renders (one cache entry, so nothing is fetched twice), held here
  // only to choose the opening tab. `ready` is the first real data — a cached head paints it at once,
  // else the first page does; a failed first read decides nothing, and the screen stays on Contacts.
  const book = useWholeContactBook();
  // "From your profile links" — read here, handed to the Contacts list, which folds a lead who is already a
  // contact into that contact's row and lists only the rest, and to the Inbox, whose rows a nameless visitor's lead
  // names ("Visitor from Wix 3.0"). Not read on the Calls tab, which draws no leads.
  const { rows: profileLeads, settled: leadsSettled } = useProfileLinkLeads(tab !== 'calls');
  useEffect(() => {
    if (!restored.current || tabDecided.current || book.kind !== 'ready') return;
    tabDecided.current = true;
    if (!tabPicked.current) setTab(defaultTab(book.contacts));
  }, [book, router.isReady]);

  // …and written back as they change. `replace`, shallow, so it adds no history entry and refetches
  // nothing — the browser's back then returns to exactly this. No tab is written until one is decided,
  // so a fresh `/contacts` still gets the default rule.
  useEffect(() => {
    if (!restored.current || onHome) return;
    const status = inboxStatus ? paramFromStatus(inboxStatus) : undefined;
    lastStatusParam.current = status;
    const next = outreachUrlQuery({ tab: tabDecided.current ? tab : undefined, search, inboxSearch, status }, router.query);
    if (sameQuery(router.query, next)) return;
    void router.replace({ pathname: router.pathname, query: next }, undefined, { shallow: true, scroll: false });
  }, [tab, search, inboxSearch, inboxStatus, router, onHome]);
  const [listState, setListState] = useState<ContactsListState>('loading');

  // The cross-note (outreach-needs-you contract rule 6): "N tasks need your attention ›", to Tasks › Action needed.
  // N is the account-wide TASKS half the island pill shows (this screen has no Space filter), so the two agree.
  const island = useIslandState();
  const tasksWaiting = island.waitingCount;
  const openTasksWaiting = () => {
    logPageClickBi({ page_name: 'contacts_page', button_name: 'tasks_waiting_note' }, { componentName: 'tasks-waiting-note' });
    void router.push({ pathname: '/tasks', query: { tab: 'waiting' } });
  };
  const [filtersSlot, setFiltersSlot] = useState<HTMLElement | null>(null);

  // `undefined` until the gate answers. A `calls` tab restored from the URL waits for it, and falls back to
  // Contacts only once the gate says the tab is off.
  const callTab = useOutreachCallTabEnabled();
  useEffect(() => {
    if (callTab === false && tab === 'calls') setTab('contacts');
  }, [callTab, tab]);

  const tabs: TabItem<OutreachTab>[] = [
    { value: 'contacts', label: t('contacts.title') },
    { value: 'inbox', label: t('outreach.tab.inbox') },
    ...(callTab === true ? [{ value: 'calls' as const, label: t('outreach.tab.calls') }] : []),
  ];

  const addContact = () => logPageClickBi({ page_name: 'contacts_page', button_name: 'add_contact' });
  // "Add a new contact" drops the user into a fresh chat with a seeded first message — the same handoff
  // the contact detail's "Reach out" uses, so adding a contact is a conversation, not a form.
  const addContactHref = chatHandoffLink({ kind: 'new', message: t('contacts.addContactSeed') }, basePath).route;
  const addContactAction = (
    <Button href={addContactHref} onClick={addContact}>
      {t('contacts.addContact')}
    </Button>
  );

  const onContacts = tab === 'contacts';
  // A `calls` tab (restored from the URL) renders only once the gate says yes: while it is unanswered the body
  // is empty, so a closed toggle never mounts the Calls tab or its reads, even for a moment.
  const onCalls = tab === 'calls' && callTab === true;
  const callsPending = tab === 'calls' && callTab !== true;
  // Desktop toolbar search + Add show on both LIST tabs once the active list is populated (loading and the
  // illustrated empty state carry their own chrome — a spinner, or the in-card CTA). Calls has neither.
  // The home's card counts a profile-link lead as someone, so it waits for that read and stands down for a lead.
  const noLeads = leadsSettled && profileLeads.length === 0;
  // The dev switch with the Calls tab on (or not yet answered): the tabs stay and the Contacts and Inbox tabs
  // show the card, as an empty account would.
  const forcedTabCard = !!onHome && isHomeEmptyForced() && callTab !== false;
  const showToolbarActions = !onCalls && !callsPending && !narrow && listState !== 'loading' && listState !== 'empty';
  const showToolbarAdd = showToolbarActions && !onHome;
  // Mobile-web pins the foot CTA on both list tabs: Contacts in every state, and the Inbox empty state (its
  // in-card CTA is desktop-only, matching the Contacts empty card). Not on Calls.
  const showFootCta = narrow && !onCalls && !callsPending && !onHome;
  // The chat bar rides above the pinned button rather than covering it.
  const footCtaRef = useRef<HTMLDivElement>(null);
  useMobileActionBarHeight(footCtaRef, showFootCta);
  // The home draws its designed empty state for an account with nobody in the book (Figma 21849:54623). The Inbox
  // reads the same head, so no contacts also means no messages. It replaces the whole page only while the Calls
  // tab is off; with it on, the tabs stay and the empty Contacts and Inbox tabs show the card, as on the phones.
  const homeEmpty =
    !!onHome &&
    callTab === false &&
    (isHomeEmptyForced() ||
      (book.kind === 'ready' &&
        book.contacts.length === 0 &&
        book.book.length === 0 &&
        !book.hasMore &&
        !book.bookUnavailable &&
        noLeads));
  // Nothing while a restored `calls` waits on the gate: the Calls copy must not show to someone outside it.
  const subtitle = callsPending ? null : onCalls ? t('outreach.calls.subtitle') : onContacts ? t('contacts.description') : t('outreach.inbox.subtitle');

  return (
    <div className={`${styles.body} ${styles.outreachRoot}`}>
      {!onHome && (
        <PageHeader
          title={t('home.map.outreach')}
          breadcrumb={[{ label: t('nav.yourAgents'), href: '/agents' }, { label: t('home.map.outreach') }]}
        />
      )}

      <CategoryPage>
        {/* No Space filter: contacts and their threads carry no workspace id, so there is nothing to
            narrow by (the same reason both phones draw none). */}
        {onHome && (
          <CategoryHeader
            title={t('categoryView.outreach.title')}
            description={homeEmpty ? undefined : <TabConnectHint tab="outreach" fallback={t('categoryView.outreach.description')} />}
            tight={homeEmpty}
          />
        )}
        {homeEmpty ? (
          <OutreachEmptyState />
        ) : (
        <>
        <CategoryToolbar
          end={
            showToolbarActions && !forcedTabCard ? (
              <>
              {/* The Inbox's Channel · Contact · Status land here (Figma 21055:254883). */}
              {!onContacts && <span ref={setFiltersSlot} className={styles.toolbarFilters} />}
              <Input
                size="medium"
                type="search"
                startIcon={<Search size={16} aria-hidden />}
                // The toolbar draws ONE field but edits the ACTIVE tab's own query, so switching tabs
                // shows that tab's search rather than carrying the other's over.
                value={onContacts ? search : inboxSearch}
                onChange={(event) => (onContacts ? setSearch : setInboxSearch)(event.target.value)}
                onClear={() => (onContacts ? setSearch : setInboxSearch)('')}
                clearAriaLabel={t('search.dialog.clear')}
                placeholder={onContacts ? t('contacts.search.placeholder') : t('outreach.inbox.search.placeholder')}
                className={styles.toolbarSearch}
              />
              </>
            ) : null
          }
          action={onHome ? action : showToolbarAdd ? addContactAction : null}
        >
          <Tabs<OutreachTab>
            variant="category"
            items={tabs}
            value={tab}
            onChange={(value) => {
              logPageClickBi({ page_name: 'contacts_page', button_name: `tab_${value}` });
              // The reader has chosen; the default rule never overrides that — and a pick is a decision,
              // so it is written to the URL even if it beat the default rule to the first read.
              tabPicked.current = true;
              tabDecided.current = true;
              setTab(value);
            }}
            label={t('home.map.outreach')}
          />
        </CategoryToolbar>

      {/* The tab's one-line explainer is the phones' (Figma 21055:255008); the desktop frame has none. */}
      {subtitle !== null && narrow ? (
        <Typography variant="p2" color="secondary" className={styles.outreachSubtitle}>
          {subtitle}
        </Typography>
      ) : null}

      {/* Under the header, above the tabs' content (contract rule 6). Hidden at zero by the row itself. */}
      {homeEmpty ? null : (
        <WaitingNoteRow
          count={tasksWaiting}
          text={tasksWaiting === 1 ? t('outreach.note.taskWaiting') : t('outreach.note.tasksWaiting', { count: tasksWaiting })}
          onOpen={openTasksWaiting}
          testId="tasks-waiting"
        />
      )}

      {callsPending ? null : onCalls ? (
        <CallsBody />
      ) : forcedTabCard ? (
        <OutreachEmptyState />
      ) : onContacts ? (
        <ContactsBody
          onOpen={(id) => router.push(contactPath(id))}
          // Lead cards from the owner's profile links (spec-gated; `[]` off or empty). The body matches them to
          // the contacts it lists and draws the section for the unmatched rest (not under a search).
          profileLeads={profileLeads}
          illustratedEmpty
          // Desktop: the CTA lives inside the empty card. Narrow: it is pinned at the foot instead.
          emptyAction={narrow ? undefined : addContactAction}
          emptyState={onHome && noLeads ? <OutreachEmptyState /> : undefined}
          onListState={setListState}
          search={search}
          onSearchChange={setSearch}
          // Desktop draws the search in the toolbar; narrow lets the body draw it full-width.
          renderSearch={narrow}
          wholeBook
        />
      ) : (
        <InboxBody
          onOpen={(id) => router.push(contactPath(id))}
          emptyAction={narrow ? undefined : addContactAction}
          emptyState={onHome && noLeads ? <OutreachEmptyState /> : undefined}
          onListState={setListState}
          search={inboxSearch}
          onSearchChange={setInboxSearch}
          renderSearch={narrow}
          filtersSlot={narrow ? undefined : filtersSlot}
          profileLeads={profileLeads}
          status={inboxStatus}
          onStatusChange={setInboxStatus}
        />
      )}
        </>
        )}

      </CategoryPage>

      {/* Native-style foot CTA on mobile-web: a full-width button pinned to the bottom over a
          transparent→canvas gradient, the same treatment the Packs/Goals sticky CTAs use. */}
      {showFootCta ? (
        <div ref={footCtaRef} className={styles.footCta}>
          <Button fullWidth href={addContactHref} startIcon={<Plus size={16} aria-hidden />} onClick={addContact}>
            {t('contacts.addContact')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** The URL's status spelling (the stage group's BI value) ↔ the Inbox filter's group id, through `STAGE_GROUPS`. */
function statusFromParam(param: OutreachStatusParam): StageGroupId | null {
  return STAGE_GROUPS.find((group) => group.bi === param)?.id ?? null;
}
function paramFromStatus(status: StageGroupId): OutreachStatusParam | undefined {
  return STAGE_GROUPS.find((group) => group.id === status)?.bi as OutreachStatusParam | undefined;
}
